/**
 * Created by Oliver Eliardsson on 2024-03-06.
 */

import { StageLayer } from "@nolimitcity/slot-launcher/bin/StageLayer";
import { AssetLoader } from "@nolimitcity/slot-game/bin/asset/AssetLoader";
import { NolimitConfig } from "@nolimitcity/slot-launcher/bin/settings/NolimitConfig";
import { SpineAssetParser } from "@nolimitcity/slot-game/bin/asset/parsers/SpineAssetParser";
import * as PIXI from "pixi.js";


export class SpinePreviewer extends PIXI.Container implements StageLayer {

    private spineParser: SpineAssetParser;

    private selectedSpine: PIXI.spine.Spine | undefined;
    private previewerWindow: Window | null = null;


    constructor(loader: AssetLoader) {
        super();
        this.name = `SpinePreviewer`;
        if (NolimitConfig.isDevMode) {
            this.spineParser = (<any>loader)._spineAssetParser;
            this.init();
        }
    }

    private getSpineNames(): string[] {
        return Object.keys((<any>this.spineParser)._spineDataCache);
    }

    private getHTML(): string {
        return `
            <html lang="en">
                <head>
                    <meta charset="UTF-8">
                    <title>Spine Viewer</title>
                </head>
                <style>
                    .caret {
                        cursor: pointer;
                        user-select: none;
                    }
                    .caret::before {
                        content: "\\25B6";
                        color: black;
                        display: inline-block;
                        margin-right: 6px;
                    }
                    .caret-down::before {
                        transform: rotate(90deg);
                    }
                    .nested {
                        display: none;
                    }
                    .active {
                        display: block;
                    }
                </style>
                <body>
                    <b>Left click animation to play on loop</b>
                    <br>
                    <b>Right click animation to play once</b>
                </body>
            </html>
        `;
    }

    private getSpineAnimations(spineName: string): string[] {
        const spine = this.spineParser.getSpineAsset(spineName);
        if (spine) {
            return spine.animations.map(a => a.name);
        } else {
            return [];
        }
    }

    private createAndDisplayAnimation(spine: string, animation: string, loop: boolean = true) {
        if (this.selectedSpine) {
            this.removeChild(this.selectedSpine);
        }
        this.selectedSpine = new PIXI.spine.Spine(this.spineParser.getSpineAsset(spine));
        this.selectedSpine.state.setAnimation(0, animation, loop);
        this.addChild(this.selectedSpine);
    }

    private createWindow(): void {
        const params = `popup=yes,scrollbars=no,resizable=no,status=no,location=no,toolbar=no,menubar=no,width=600,height=300,left=100,top=100`;
        this.previewerWindow = window.open(undefined, `SpinePreviewer`, params);
        if (this.previewerWindow) {
            const document: Document = this.previewerWindow.document;

            this.previewerWindow.document.body.insertAdjacentHTML(`afterbegin`, this.getHTML());
            let childClicked = false;
            this.getSpineNames().forEach(name => {
                const spineNode = document.createElement(`div`);
                spineNode.className = `caret`;
                spineNode.textContent = name;
                spineNode.addEventListener(`click`, () => {
                    if (!childClicked) {
                        spineNode.querySelector(`.nested`)?.classList.toggle(`active`);
                    } else {
                        childClicked = false;
                    }
                });

                const nestedNode = document.createElement(`ul`);
                nestedNode.className = `nested`;
                this.getSpineAnimations(name).forEach((animation, index) => {
                    const animationNode = document.createElement(`button`);
                    animationNode.addEventListener(`click`, () => {
                        childClicked = true;
                        this.createAndDisplayAnimation(name, animation);
                    });
                    animationNode.addEventListener(`contextmenu`, (ev) => {
                        ev.preventDefault();
                        childClicked = true;
                        this.createAndDisplayAnimation(name, animation, false);
                        return false;
                    });
                    animationNode.textContent = animation;
                    nestedNode.appendChild(animationNode);
                });
                spineNode.appendChild(nestedNode);

                document.body.appendChild(spineNode);
            });

            // Popup windows does not emit a closed event so we have to do this hackjob to detect if window has been closed..
            const timer = setInterval(() => {
                if (this.previewerWindow?.closed) {
                    clearInterval(timer);
                    this.previewerWindow = null;
                    if (this.selectedSpine) {
                        this.removeChild(this.selectedSpine);
                        this.selectedSpine = undefined;
                    }
                }
            }, 1000);
        }
    }

    private init() {
        this.position.set(360, 360);

        document.addEventListener(`keydown`, (e) => {
            if (e.key == `p` || e.key == `P`) {
                if (this.previewerWindow) {
                    this.previewerWindow.focus();
                } else {
                    this.createWindow();
                }
            }
        });
    }
}